import { NO_CHANNELS_SELECTED } from "@/v2/components/filters/types";
import type { Channel } from "../Events/types";

/**
 * The channel filter value follows the same convention as the Events page
 * sidebar (FilterSidebar / api.ts buildScheduleParams): "" means every
 * channel (no filter sent to the API), a comma-separated id list means only
 * those channels, and NO_CHANNELS_SELECTED means every channel was manually
 * deselected (Clear All) - show nothing, without asking the API to filter on
 * a made-up channel id.
 */
export { NO_CHANNELS_SELECTED };

function channelIds(channels: Channel[]): string[] {
  return channels.map((c) => String(c.channel_id));
}

export function isChannelSelected(value: string, channelId: string): boolean {
  if (value === NO_CHANNELS_SELECTED) return false;
  if (!value) return true;
  return value.split(",").includes(channelId);
}

/** Toggle one channel on/off, collapsing to the "all" or "none" sentinel at the edges. */
export function toggleChannelSelection(
  channels: Channel[],
  value: string,
  channelId: string,
): string {
  const allIds = channelIds(channels);
  const selected = new Set(
    value === NO_CHANNELS_SELECTED ? [] : value ? value.split(",") : allIds,
  );
  if (selected.has(channelId)) selected.delete(channelId);
  else selected.add(channelId);

  if (selected.size === 0) return NO_CHANNELS_SELECTED;
  if (selected.size === allIds.length) return "";
  return allIds.filter((id) => selected.has(id)).join(",");
}

/** Short label for the dropdown trigger. */
export function channelFilterSummary(channels: Channel[], value: string): string {
  if (value === NO_CHANNELS_SELECTED) return "No channels";
  if (!value) return "All Channels";
  const ids = value.split(",");
  if (ids.length === 1) {
    const channel = channels.find((c) => String(c.channel_id) === ids[0]);
    return channel?.channel_name ?? "1 Channel";
  }
  return `${ids.length} Channels`;
}

/**
 * The filter value as sent to an API, scoped to the channels the scheduler
 * supports: "all channels" becomes exactly those ids (the feeds would
 * otherwise return every tenant channel), a selection keeps only supported
 * ids, and a selection with none left is "no channels".
 */
export function scopeToChannels(value: string, supportedIds: number[]): string {
  if (value === NO_CHANNELS_SELECTED) return NO_CHANNELS_SELECTED;
  const supported = supportedIds.map(String);
  if (!value) return supported.join(",");
  const kept = value.split(",").filter((id) => supported.includes(id));
  return kept.length ? kept.join(",") : NO_CHANNELS_SELECTED;
}
